import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogOut, LayoutDashboard, Server, Settings, Activity, Users, FolderArchive, Smartphone, Target } from 'lucide-react';
import { motion } from 'framer-motion';

const DashboardLayout = () => {
    const { logout, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const [activeTab, setActiveTab] = useState(() => {
        if (location.pathname.includes('/bibliotheque-de-ressources')) return 'bibliotheque';
        if (location.pathname.includes('/clients')) return 'clients';
        if (location.pathname.includes('/prospects')) return 'prospects';
        if (location.pathname.includes('/app-mobile')) return 'app-mobile';
        return 'dashboard';
    });

    React.useEffect(() => {
        if (location.pathname.includes('/bibliotheque-de-ressources')) setActiveTab('bibliotheque');
        else if (location.pathname.includes('/clients')) setActiveTab('clients');
        else if (location.pathname.includes('/prospects')) setActiveTab('prospects');
        else if (location.pathname.includes('/app-mobile')) setActiveTab('app-mobile');
        else if (location.pathname.includes('/dashboard')) setActiveTab('dashboard');
    }, [location.pathname]);


    const navItems = [
        { id: 'dashboard', name: 'Vue Globale', icon: <LayoutDashboard size={16} />, path: '/dashboard' },
        { id: 'clients', name: 'Clients', icon: <Users size={16} />, path: '/clients' },
        { id: 'prospects', name: 'Prospects', icon: <Target size={16} />, path: '/prospects' },
        { id: 'app-mobile', name: 'App Mobile Globale', icon: <Smartphone size={16} />, path: '/app-mobile' },
        { id: 'bibliotheque', name: 'Bibliothèque de Ressources', icon: <FolderArchive size={16} />, path: '/bibliotheque-de-ressources' },
        { id: 'topology', name: 'Topologie', icon: <Server size={16} />, path: null },
        { id: 'flux', name: 'Flux', icon: <Activity size={16} />, path: null },
        { id: 'config', name: 'Configuration', icon: <Settings size={16} />, path: null },
    ];


    return (
        <div className="min-h-screen bg-slate-50 flex font-sans selection:bg-blue-100 selection:text-blue-900">
            {/* Left Blueprint Pane */}
            <aside className="w-[220px] h-screen shrink-0 bg-white border-r border-slate-200 flex flex-col fixed top-0 left-0 z-50 shadow-[4px_0_24px_-12px_rgba(0,0,0,0.05)]">

                {/* Branding Block */}
                <div className="h-12 border-b border-slate-200 px-4 flex items-center shrink-0 w-full relative overflow-hidden group hover:bg-slate-50 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3 relative z-10 w-full">
                        <img src="/logo.png" alt="Logo" className="w-7 h-7" />
                        <div className="flex flex-col">
                            <span className="text-xs font-black text-slate-900 tracking-tight leading-none mb-0.5">XORA ORCHESTRATOR</span>
                            <div className="flex items-center gap-1">
                                <div className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse"></div>
                                <span className="text-[8px] font-black text-emerald-600 uppercase tracking-widest leading-none">Réseau Actif</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Primary Nav */}
                <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
                    <div className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 mb-3 px-2">Navigation Racine</div>
                    {navItems.map((item) => {
                        const isActive = activeTab === item.id;
                        return (
                            <button
                                key={item.id}
                                onClick={() => {
                                    if (item.onClick) {
                                        item.onClick();
                                        return;
                                    }
                                    if (item.path) { navigate(item.path); }
                                    setActiveTab(item.id);
                                }}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-none text-left transition-all border-l-2 ${isActive
                                    ? 'bg-blue-50/50 border-blue-600 text-blue-700 font-bold'
                                    : 'border-transparent text-slate-600 font-medium hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300'
                                    }`}
                            >
                                <span className={`${isActive ? 'text-blue-600' : 'text-slate-400'}`}>
                                    {item.icon}
                                </span>
                                <span className="text-[11px] uppercase tracking-wider">{item.name}</span>
                            </button>
                        );
                    })}
                </nav>

                {/* Identity / Session Block */}
                <div className="border-t border-slate-200 p-3 bg-slate-50/50 shrink-0">
                    <div className="flex justify-between items-end">
                        <div className="space-y-1.5 w-full">
                            <div className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Session Administrateur</div>
                            <div className="text-[11px] font-bold text-slate-900 font-mono truncate">{user?.email}</div>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="text-slate-400 hover:text-rose-600 transition-colors p-2"
                            title="Quitter la session"
                        >
                            <LogOut size={16} strokeWidth={2.5} />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Right Work Area */}
            <main className="flex-1 min-w-0 flex flex-col ml-[220px] bg-slate-50 relative min-h-screen">
                <div className="h-12 bg-white border-b border-slate-200 px-6 flex items-center justify-between shrink-0 sticky top-0 z-40">
                    {/* Header Path */}
                    <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                        <span>XORA</span>
                        <span>/</span>
                        <span className="text-slate-900">
                            {activeTab === 'clients' ? 'Clients' : activeTab === 'prospects' ? 'Prospects' : activeTab === 'bibliotheque' ? 'Bibliothèque de Ressources' : activeTab === 'app-mobile' ? 'App Mobile Globale' : 'Dashboard'}
                        </span>
                    </div>
                </div>

                {/* Router Target & Tab Views */}
                <div className="flex-1 flex flex-col relative min-h-0">
                    <div className={['dashboard', 'clients', 'prospects', 'bibliotheque', 'app-mobile'].includes(activeTab) ? 'flex-1 overflow-x-hidden' : 'hidden'}>
                        <Outlet />
                    </div>


                    {activeTab === 'topology' && (
                        <div className="flex-1 flex items-center justify-center bg-white p-8 overflow-y-auto">
                            <div className="text-center max-w-md">
                                <Server size={48} className="mx-auto text-slate-300 mb-6" />
                                <h2 className="text-lg font-black text-slate-900 uppercase tracking-widest mb-2">Topologie Réseau</h2>
                                <p className="text-sm font-medium text-slate-500">Outil de cartographie globale et de pilotage des pools applicatifs en cours de développement.</p>
                            </div>
                        </div>
                    )}

                    {activeTab === 'flux' && (
                        <div className="flex-1 flex items-center justify-center bg-white p-8 overflow-y-auto">
                            <div className="text-center max-w-md">
                                <Activity size={48} className="mx-auto text-slate-300 mb-6" />
                                <h2 className="text-lg font-black text-slate-900 uppercase tracking-widest mb-2">Flux d'Activité</h2>
                                <p className="text-sm font-medium text-slate-500">Moteur d'analyse des métriques en temps réel et de l'orchestration des données systèmes.</p>
                            </div>
                        </div>
                    )}

                    {activeTab === 'config' && (
                        <div className="flex-1 flex items-center justify-center bg-white p-8 overflow-y-auto">
                            <div className="text-center max-w-md">
                                <Settings size={48} className="mx-auto text-slate-300 mb-6" />
                                <h2 className="text-lg font-black text-slate-900 uppercase tracking-widest mb-2">Paramètres Multi-Tenants</h2>
                                <p className="text-sm font-medium text-slate-500 mb-6">Configuration maître de l'architecture, gestion des droits et stratégies de déploiement automatique.</p>
                                <button
                                    onClick={() => navigate('/app-mobile')}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-600/20 transition cursor-pointer"
                                >
                                    <Smartphone size={16} />
                                    <span>Gérer l'App Mobile Globale</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default DashboardLayout;
